import React, { useState, useEffect, useCallback } from 'react';
import SampleCardsSection from './SampleCardsSection';
// Import components
import Calendar from './Calendar';
import StatusChart from './StatusChart';
// Import styles
import './SampleManager.css';

const SampleManager = ({ user }) => {
    const [samples, setSamples] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState(null);
    const [filteredSamples, setFilteredSamples] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshCount, setRefreshCount] = useState(0);

    // Fetch samples data function (extracted so it can be reused for refresh)
    const fetchSamples = useCallback(async () => {
        console.log('SampleManager: Starting fetchSamples');
        try {
            setIsLoading(true);
            const response = await fetch('https://service1.szapfs.org/reqvalidorders');
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('SampleManager: Fetched data successfully:', data.length, 'samples');
            
            // Transform the data to match our application's structure
            const transformedData = data.map(order => ({
                patientId: order.patientId || `PAT-${order.barcode.substring(3)}`,
                barcode: order.barcode,
                firstName: order.firstName,
                lastName: order.lastName,
                address: order.address,
                kitType: order.kitType,
                status: {
                    currentStage: order.currentStage,
                    stages: {
                        "Order Received": order.order_received,
                        "Sent Cargo": order.sent_cargo,
                        "Cargo Delivered": order.cargo_delivered,
                        "Lab Received": order.lab_received,
                        "Data Delivered": order.data_delivered
                    }
                }
            }));
            
            console.log('SampleManager: Setting samples state with', transformedData.length, 'samples');
            setSamples(transformedData);
            setError(null);
        } catch (err) {
            console.error("Error fetching samples:", err);
            setError("Failed to load samples. Please try again later.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Initial data fetch
    useEffect(() => {
        fetchSamples();
    }, [fetchSamples]);

    // Refresh data function that will be passed down to components
    const handleRefreshData = useCallback(() => {
        console.log('SampleManager: Refreshing data');
        // First increment refresh count to trigger Calendar refresh
        setRefreshCount(prev => prev + 1);
        
        // Fetch new data immediately instead of delaying
        fetchSamples();
    }, [fetchSamples]);

    // Filter samples based on search term and date
    useEffect(() => {
        let filtered = [...samples];
        
        // Apply search term filter
        if (searchTerm.trim() !== '') {
            const searchTermLower = searchTerm.toLowerCase();
            filtered = filtered.filter(sample => 
                (sample.patientId && sample.patientId.toLowerCase().includes(searchTermLower)) ||
                sample.barcode.toLowerCase().includes(searchTermLower) ||
                sample.firstName.toLowerCase().includes(searchTermLower) ||
                sample.lastName.toLowerCase().includes(searchTermLower) ||
                sample.kitType.toLowerCase().includes(searchTermLower)
            );
        }
        
        // Apply date filter if available
        if (dateFilter) {
            filtered = filtered.filter(sample => {
                // Get the selected date normalized to start of day
                const filterDate = new Date(dateFilter);
                filterDate.setHours(0, 0, 0, 0);
                const filterTime = filterDate.getTime();
                
                // Check if any stage occurred on the selected date
                const stages = sample.status.stages;
                
                // Check if any specific stage date matches the filter date
                return Object.values(stages).some(stageDate => {
                    if (!stageDate) return false;
                    
                    const date = new Date(stageDate);
                    date.setHours(0, 0, 0, 0);
                    return date.getTime() === filterTime;
                });
            });
        }
        
        setFilteredSamples(filtered);
    }, [samples, searchTerm, dateFilter]);

    return (
        <div className="samplemanager-container">
            {isLoading && samples.length === 0 ? (
                <div className="samplemanager-loading">Loading samples...</div>
            ) : error && samples.length === 0 ? (
                <div className="samplemanager-error">{error}</div>
            ) : (
                <>
                    <div className="samplemanager-container-left">
                        <div className="samplemanager-container-left-top">
                            <Calendar 
                                onDateSelect={setDateFilter} 
                                onSearch={setSearchTerm}
                                dateFilter={dateFilter}
                                searchTerm={searchTerm}
                                refreshTrigger={refreshCount}
                                onRefreshData={handleRefreshData}
                            />
                        </div>
                        <div className="samplemanager-container-left-bottom">
                            <SampleCardsSection 
                                samples={filteredSamples}
                                onRefreshData={handleRefreshData}
                            />
                        </div>
                    </div>
                    <div className="samplemanager-container-right">
                        <StatusChart 
                            samples={samples}
                            filteredSamples={filteredSamples}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default SampleManager;
